
import { animate, motion, useMotionValue } from 'motion/react';
import React, { useState } from 'react';

interface CategoryBoxProps {
    prefab: 'products' | 'services'
}

interface prefab {
    categoryName: string,
    backgroundImage: string,
    icon: React.JSX.Element,
    to?: string
}

const IconTruck: React.FC = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 640 640"
            className='size-24'
            fill='#b91c1c'>
            <path d="M96 144C87.2 144 80 151.2 80 160L80 448C80 456.8 87.2 464 96 464L99.3 464C109.7 427.1 143.7 400 184 400C224.3 400 258.2 427.1 268.7 464L371.3 464C376.2 446.6 386.4 431.3 400 420.1L400 160C400 151.2 392.8 144 384 144L96 144zM99.3 512L96 512C60.7 512 32 483.3 32 448L32 160C32 124.7 60.7 96 96 96L384 96C419.3 96 448 124.7 448 160L448 192L503.4 192C520.4 192 536.7 198.7 548.7 210.7L589.3 251.3C601.3 263.3 608 279.6 608 296.6L608 448C608 483.3 579.3 512 544 512L540.7 512C530.3 548.9 496.3 576 456 576C415.7 576 381.8 548.9 371.3 512L268.7 512C258.3 548.9 224.3 576 184 576C143.7 576 109.8 548.9 99.3 512zM448 320L560 320L560 296.6C560 292.4 558.3 288.3 555.3 285.3L514.7 244.7C511.7 241.7 507.6 240 503.4 240L448 240L448 320zM448 368L448 400.4C450.6 400.2 453.3 400 456 400C496.3 400 530.2 427.1 540.7 464L544 464C552.8 464 560 456.8 560 448L560 368L448 368zM184 528C206.1 528 224 510.1 224 488C224 465.9 206.1 448 184 448C161.9 448 144 465.9 144 488C144 510.1 161.9 528 184 528zM456 528C478.1 528 496 510.1 496 488C496 465.9 478.1 448 456 448C433.9 448 416 465.9 416 488C416 510.1 433.9 528 456 528z" />
        </svg>
    )
}

const IconTablet: React.FC = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            className='size-24'
            fill='#b91c1c'>
            <path d="M64 48c-8.8 0-16 7.2-16 16V448c0 8.8 7.2 16 16 16H384c8.8 0 16-7.2 16-16V64c0-8.8-7.2-16-16-16H64zM0 64C0 28.7 28.7 0 64 0H384c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V64zM192 400h64c8.8 0 16 7.2 16 16s-7.2 16-16 16H192c-8.8 0-16-7.2-16-16s7.2-16 16-16z" />
        </svg>
    )
}

const prefabs: { [key: string]: prefab } = {
    'products': {
        categoryName: 'Our Products',
        backgroundImage: '/thg/graphics/black_boogie_board_2.png',
        icon: <IconTruck />
    },
    'services': {
        categoryName: 'Our Services',
        backgroundImage: '/thg/photo/tima-miroshnichenko.jpg',
        icon: <IconTablet />
    }
}

const TryhardCategoryBox: React.FC<CategoryBoxProps> = ({ prefab }) => {
    const { categoryName, icon, backgroundImage } = prefabs[prefab];
    const filterOpacity = useMotionValue(.2);
    const iconOpacity = useMotionValue(1);
    const imageOpacity = useMotionValue(.2);
    const tabOpacity = useMotionValue(.25);
    const textColor = useMotionValue('#991b1b');
    const [hovered, setHovered] = useState(false)

    const handleOnHoverStart = () => {
        animate(filterOpacity, .2);
        animate(iconOpacity, 0);
        animate(imageOpacity, 1);
        animate(tabOpacity, 0);
        animate(textColor, '#FFFFFF')
    }

    const handleOnHoverEnd = () => {
        animate(filterOpacity, .2);
        animate(iconOpacity, 1);
        animate(imageOpacity, .2);
        animate(tabOpacity, .25);
        animate(textColor, '#991b1b');
    }

    return (
        <motion.div
            className='w-72 h-96 flex justify-center bg-white relative hover:cursor-pointer'
            style={{
                filter: 'drop-shadow(0px 4px 12px rgba(0, 0, 0, .2))',
            }}
            onHoverStart={handleOnHoverStart}
            onHoverEnd={handleOnHoverEnd}
            onPointerEnter={() => setHovered(true)}
            onPointerLeave={() => setHovered(false)}
        >
            <motion.div
                className='size-full flex justify-center items-center absolute z-10'
                style={{ opacity: imageOpacity }}
            >
                <motion.img
                    className={'object-cover ' + (prefab === 'services' ? 'size-full' : 'h-3/4')} src={backgroundImage} />
            </motion.div>

            <motion.div
                layout
                className='flex flex-col items-center justify-center gap-y-2 py-4 z-30 relative'
                style={{ alignSelf: hovered ? 'flex-end' : 'auto', transition: 'ease-out' }}
            >
                <motion.div layout style={{ opacity: iconOpacity }}>{icon}</motion.div>
                <motion.h2
                    layout
                    className='text-teko italic font-semibold text-5xl'
                    style={{
                        filter: 'drop-shadow(0px 4px 12px rgba(0, 0, 0, .5))',
                        color: textColor
                    }}
                >{categoryName}</motion.h2>
            </motion.div>

            {/* Filter */}
            <motion.div
                className='size-full bg-gradient-to-b from-black to-red-600 top-0 left-0 z-10 absolute'
                style={{ opacity: filterOpacity }}
            />

            {/* Tab */}
            <motion.svg
                className='h-[25%] bottom-0 right-0 z-20 absolute'
                style={{ opacity: tabOpacity }}
                version="1.1"
                id="Layer_1"
                xmlns="http://www.w3.org/2000/svg"
                x="0px"
                y="0px"
                viewBox="0 0 122.88 122.87"
            >
                <polygon
                    fill='white'
                    points="122.87,0 122.88,122.86 0,122.87 122.87,0" />
            </motion.svg>
        </motion.div>
    )
}

export default TryhardCategoryBox;